import { format, utcToZonedTime } from 'date-fns-tz';
import { makeStyles } from '@material-ui/styles';

import { MainButton, Text, UserIcon, Bubble } from '@bizzabo/design-system';
import { UserData, UserPreview } from '@bizzabo/entities';

import { ScheduledMeeting, LocationType } from '../../types';
import AddToCalendarButton from './AddToCalendarButton';
import { timezoneToAbbrString } from '../../../utils/date';
import currentTimeZoneIdAtom from '../../timeZoneId';
import { useAtomValue } from 'jotai';

export interface ScheduledConfirmationProps {
    meeting: ScheduledMeeting;
    currentUser: UserData;
    otherUser: UserPreview;
    sendMessage: () => void;
}

const ScheduledConfirmation: React.FC<ScheduledConfirmationProps> = ({
    meeting,
    currentUser,
    otherUser,
    sendMessage,
}) => {
    const timezoneId = useAtomValue(currentTimeZoneIdAtom);
    const timeZone = timezoneToAbbrString(timezoneId);

    const startTime = utcToZonedTime(meeting.startDate, timezoneId);
    const timeFrom = format(startTime, 'h:mm', { timeZone });
    const timeTo = format(utcToZonedTime(meeting.endDate, timezoneId), 'h:mmaaa', {
        timeZone,
    });

    const classes = useStyles();

    return (
        <div className={classes.root}>
            <div className={classes.avatars}>
                <UserIcon size="Large" photoUrl={currentUser.photoUrl} />
                <div className={classes.otherUserAvatar}>
                    <UserIcon size="Large" photoUrl={otherUser.photoUrl} />
                </div>
            </div>
            <div className={classes.title}>
                <Text variant="t50">Your meeting with {otherUser.firstName} is confirmed 🎉</Text>
            </div>
            <Text variant="t71" className={classes.centerText}>
                {format(startTime, 'EEEE, MMM. d')}, {timeFrom} - {timeTo} {timeZone}
            </Text>
            {meeting.locationType === LocationType.Physical && (
                <Text className={classes.centerText} variant="t70">
                    <Text variant="t71" className={classes.introLocation}>
                        At{' '}
                    </Text>
                    {meeting.physicalLocation}
                </Text>
            )}
            <div className={classes.confirmationControls}>
                <AddToCalendarButton
                    buttonClassName={classes.ctaButtons}
                    meeting={meeting}
                    otherUser={otherUser}
                />
                <MainButton
                    className={classes.ctaButtons}
                    before={<Bubble className={classes.bubbleIcon} />}
                    onClick={sendMessage}
                >
                    Send message
                </MainButton>
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        root: {
            ...theme.typography.t61,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        avatars: {
            display: 'flex',
        },
        otherUserAvatar: {
            marginLeft: -15,
        },
        title: {
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 6,
        },
        centerText: {
            textAlign: 'center',
        },
        introLocation: {
            display: 'inline',
        },
        confirmationControls: {
            display: 'flex',
            marginTop: 20,
        },
        bubbleIcon: {
            width: 14,
        },
        ctaButtons: {
            ...theme.typography.t80,
            height: 26,
            margin: '0 10px',
        },
    }),
    { classNamePrefix: 'schedule-confirmation' }
);

export default ScheduledConfirmation;
